import { CACHE_PROFILES } from "@/config/query-cache"
import { useTransportQuery } from "@/transport"

import type { ConversationClusteringRunOutputV1 } from "@/types_api/backend/transport/get_latest_conversation_clustering_run_output_response"
import type { ConversationClusteringRunOutputV2 } from "@/types_api/backend/transport/get_latest_failure_pattern_run_output_response"

export function useLatestConversationClusteringRunTransport({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const failureRun = useTransportQuery<
    "getLatestFailurePatternRunOutput",
    ConversationClusteringRunOutputV2
  >({
    operation: "getLatestFailurePatternRunOutput",
    request: {},
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      retry: false,
      select: (response) => response.conversation_clustering_run_output,
    },
  })
  const hasNoFailureRun = failureRun.isError && failureRun.error.status === 404
  const legacyRun = useTransportQuery<
    "getLatestConversationClusteringRunOutput",
    ConversationClusteringRunOutputV1
  >({
    operation: "getLatestConversationClusteringRunOutput",
    request: {},
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled && hasNoFailureRun,
      // A missing run (404) or a run without output (409) is an expected
      // answer, not a transient failure; callers fall back immediately.
      retry: false,
      select: (response) => response.conversation_clustering_run_output,
    },
  })

  return {
    data: hasNoFailureRun ? legacyRun.data : failureRun.data,
    error: hasNoFailureRun ? legacyRun.error : failureRun.error,
    isError: hasNoFailureRun ? legacyRun.isError : failureRun.isError,
    isPending: failureRun.isPending || (hasNoFailureRun && legacyRun.isPending),
    refetch: hasNoFailureRun ? legacyRun.refetch : failureRun.refetch,
  }
}
